import { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCwIcon } from 'lucide-react';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchIntegrations, refreshIntegration } from '../adminApi';
import { useCan } from '../useCan';
import { integrationInfo } from '../../data/insightIntegrations';
import { errorMessage } from '../../core/errors';
import { dateOf, dayShort, timeOf } from '../../core/format/dates';
import { btnGhost } from '../ui/styles';
import type { IntegrationId, IntegrationStatus } from '../../core/types/insights';
const order: IntegrationId[] = ['umami', 'searchConsole', 'googleBusiness', 'instagram', 'brevo'];
const stateText = (s: IntegrationStatus) => s.status === 'niet-gekoppeld' ? 'Niet gekoppeld' : s.status === 'fout' ? 'Ophalen mislukt' : s.updatedAt ? `Bijgewerkt tot ${dayShort(dateOf(s.updatedAt))}, ${timeOf(s.updatedAt)}` : 'Gekoppeld';

/**
 * De externe bronnen in één regel: gekoppeld, tot wanneer bijgewerkt, en "Opnieuw ophalen".
 * De server haalt elke nacht op; dit zet een extra ophaalronde in de wachtrij.
 */
export function IntegrationStatusBar() {
  // Koppelingen beheren is een eigen bevoegdheid (Marketing heeft hem niet): dan geen verzoek dat 403 geeft.
  const {
    canAll
  } = useCan();
  return canAll('integrations.manage') ? <IntegrationStatusList data-id="element-1876" data-mp="339lyjilwowv0" /> : null;
}
function IntegrationStatusList() {
  const {
    data,
    setData,
    status
  } = useLatestLoad(fetchIntegrations);
  const [busy, setBusy] = useState<IntegrationId | null>(null);
  const refresh = async (id: IntegrationId) => {
    if (busy) return;
    setBusy(id);
    try {
      const next = await refreshIntegration(id);
      setData(d => d ? {
        ...d,
        [id]: next
      } : d);
      toast.success(`${integrationInfo[id].name}: opnieuw opgehaald`);
    } catch (err) {
      toast.error('Opnieuw ophalen lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(null);
    }
  };
  if (!data) return status === 'error' ? <p className="text-[13px] text-muted" data-id="element-1877" data-mp="3w0m786v6mot0">De status van de koppelingen kon niet laden.</p> : null;
  return <details className="group rounded-lg border border-line bg-white" data-id="element-1878" data-mp="1vy4jkp4avcxt">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 text-[13px] sm:px-5" data-id="element-1879" data-mp="1ru53u8qugi5i">
        <span className="text-ink" data-id="element-1880" data-mp="25s3h1atfi8jf">Koppelingen</span>
        {order.map(id => <span key={id} className="inline-flex items-center gap-1.5 text-muted" data-id="element-1881" data-mp="20ta35ha8cfiq">
            <span className={`h-1.5 w-1.5 rounded-full ${data[id].status === 'gekoppeld' ? 'bg-sage' : data[id].status === 'fout' ? 'bg-clay' : 'border border-muted'}`} aria-hidden="true" data-id="element-1882" data-mp="1d6bo8o1rdfmx" />
            {integrationInfo[id].name.replace(/ \(.*\)$/, '')}
            <span className="sr-only" data-id="element-1883" data-mp="1i43jns1m3jvv">: {stateText(data[id])}</span>
          </span>)}
        <span className="ml-auto text-[12px] text-muted group-open:hidden" data-id="element-1884" data-mp="udzpebabcwhj">Details</span>
      </summary>
      <ul className="divide-y divide-line border-t border-line" data-id="element-1885" data-mp="pfela0rxxsnt">
        {order.map(id => {
        const s = data[id];
        return <li key={id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-[13px] sm:px-5" data-id="element-1886" data-mp="35u9o95cytwxb">
              <span className="min-w-[12rem] text-ink" data-id="element-1887" data-mp="2s3en6f8pkk4a">{integrationInfo[id].name}</span>
              <span className={`flex-1 ${s.status === 'fout' ? 'text-clay' : 'text-muted'}`} data-id="element-1888" data-mp="2w4gf0b0aptw8">
                {stateText(s)}
                {s.message && <span className="block text-[12px] text-muted" data-id="element-1889" data-mp="3jvcc9nnithav">{s.message}</span>}
              </span>
              {s.status !== 'niet-gekoppeld' && <button type="button" onClick={() => void refresh(id)} disabled={busy !== null} className={`${btnGhost} disabled:opacity-50`} data-id="element-1890" data-mp="3atcswop1a14r">
                  <RefreshCwIcon className={`h-3.5 w-3.5 ${busy === id ? 'animate-spin' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-1891" data-mp="3fr4ktipohn8y" />
                  {busy === id ? 'Ophalen…' : 'Opnieuw ophalen'}
                </button>}
            </li>;
      })}
      </ul>
    </details>;
}